import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import IconButton from '../ui/IconButton';
import { Plus } from 'lucide-react';
import OfferingCard from './OfferingCard';

const OfferingsForm = ({ initialData, onSave, isLoading, onDelete }) => {
  // Handle both formats: direct array or object with offerings property
  const getOfferingsArray = (data) => {
    if (Array.isArray(data)) {
      return data.length > 0 ? data : [{ id: '', name: '', description: '', page_url: '' }];
    }
    if (data?.offerings && Array.isArray(data.offerings)) {
      return data.offerings.length > 0
        ? data.offerings
        : [{ id: '', name: '', description: '', page_url: '' }];
    }
    return [{ id: '', name: '', description: '', page_url: '' }];
  };

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      offerings: getOfferingsArray(initialData),
    },
  });

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'offerings',
  });

  const onSubmit = (index, data) => {
    onSave(index, data);
  };

  const [draggedIndex, setDraggedIndex] = useState(null);
  const [draggedOverIndex, setDraggedOverIndex] = useState(null);

  // Watch all form values to detect changes
  const offerings = watch('offerings');

  // Update form when initialData changes
  useEffect(() => {
    if (initialData) {
      reset({ offerings: getOfferingsArray(initialData) });
    }
  }, [initialData, reset]);

  const handleSaveOffering = (index, updatedOffering) => {
    // save the single offering change
    handleSubmit((data) => onSubmit(index, updatedOffering))();
  };

  const handleDeleteOffering = (id) => {
    if (fields.length > 1) {
      const index = offerings.findIndex((offering) => offering.id === id);
      remove(index);
      // Auto-save after deletion
      handleSubmit(() => onDelete(id))();
    }
  };

  const handleAddOffering = () => {
    append({ id: '', name: '', description: '', page_url: '' });
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== index) {
      setDraggedOverIndex(index);
    }
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    if (draggedIndex !== index) {
      setDraggedOverIndex(index);
    }
  };

  const handleDragLeave = (e, index) => {
    e.preventDefault();
    setDraggedOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDraggedOverIndex(null);
      return;
    }

    // Use react-hook-form's move function
    move(draggedIndex, dropIndex);

    // Save the reordered offerings
    const newOfferings = [...offerings];
    const draggedItem = newOfferings[draggedIndex];
    newOfferings.splice(draggedIndex, 1);
    newOfferings.splice(dropIndex, 0, draggedItem);

    onSave({ offerings: newOfferings });

    setDraggedIndex(null);
    setDraggedOverIndex(null);
  };

  return (
    <div className='space-y-4  py-4 pl-0 lg:pl-6 lg:border-l  border-gray-300'>
      <div className='flex items-center justify-between'>
        <div>
          <h3 className='text-lg font-semibold text-gray-900'>Products & Services</h3>
          <p className='text-sm text-gray-500 mt-1'>Manage your offerings and pricing</p>
        </div>
        <IconButton
          icon={Plus}
          tooltip='Add offering'
          onClick={handleAddOffering}
          variant='primary'
        />
      </div>

      <div className='space-y-4'>
        {fields.map((field, index) => (
          <OfferingCard
            key={field.id}
            offering={offerings[index]}
            index={index}
            onSave={handleSaveOffering}
            onDelete={fields.length > 1 ? handleDeleteOffering : null}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            isDragging={draggedIndex === index}
            isDraggedOver={draggedOverIndex === index}
          />
        ))}
      </div>
    </div>
  );
};

export default OfferingsForm;
